import { useEffect, useState, useCallback } from "react";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", icon_url: "" });
  const [openForm, setOpenForm] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/categories");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ name: "", icon_url: "" }); setOpenForm(true); };
  const openEdit = (c) => { setEditing(c.id); setForm({ name: c.name, icon_url: c.icon_url || "" }); setOpenForm(true); };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/admin/categories/${editing}`, form);
        toast.success("Category updated");
      } else {
        await api.post("/admin/categories", form);
        toast.success("Category added");
      }
      setOpenForm(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/admin/categories/${delTarget}`);
      toast.success("Category deleted");
      setDelTarget(null); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setDelTarget(null);
    }
  };

  return (
    <AdminLayout title="Categories" subtitle={`${items.length} specialization${items.length === 1 ? "" : "s"}`} action={
      <Button data-testid="admin-add-cat-btn" onClick={openCreate} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Add Category</Button>
    }>
      <div data-testid="admin-categories-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((c) => (
          <div key={c.id} data-testid={`cat-card-${c.id}`} className="bg-white border border-border rounded-2xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {c.icon_url ? <img src={c.icon_url} alt="" className="h-10 w-10 rounded-xl object-cover" /> : <span className="h-10 w-10 rounded-xl bg-secondary" />}
              <div className="font-medium">{c.name}</div>
            </div>
            <div>
              <Button size="sm" variant="ghost" data-testid={`cat-edit-${c.id}`} onClick={() => openEdit(c)}><Edit2 className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" data-testid={`cat-del-${c.id}`} onClick={() => setDelTarget(c.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent data-testid="cat-form-modal" className="sm:max-w-md rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><label className="text-xs font-medium">Name</label>
              <Input data-testid="cat-form-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1.5"><label className="text-xs font-medium">Icon URL (optional)</label>
              <Input data-testid="cat-form-icon" value={form.icon_url} onChange={(e) => setForm((f) => ({ ...f, icon_url: e.target.value }))} placeholder="https://..." />
              {form.icon_url && <img src={form.icon_url} alt="" className="mt-1 h-12 w-12 rounded-xl object-cover" />}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenForm(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="cat-save-btn" onClick={save} disabled={!form.name} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground">{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => { if (!o) setDelTarget(null); }}>
        <AlertDialogContent data-testid="cat-del-modal">
          <AlertDialogHeader><AlertDialogTitle>Delete this category?</AlertDialogTitle>
            <AlertDialogDescription>If doctors are assigned to it, deletion will fail with a count.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Keep</AlertDialogCancel>
            <AlertDialogAction data-testid="cat-confirm-del" onClick={del} className="rounded-full bg-rose-600 hover:bg-rose-600/90 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
